<template>
    <ValidationObserver ref="observer" tag="div">
        <p class="tw:text-sm tw:text-slate-600 tw:mb-6 tw:leading-relaxed">
            Puedes arrastrar los archivos o hacer clic en cada zona para seleccionarlos.
        </p>

        <div class="tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-6">
            <!-- ── Zona 1: Documentos DNI/NIE/Pasaporte ── -->
            <div>
                <h4 class="tw:text-sm tw:font-semibold tw:text-slate-700 tw:mb-2">
                    Documentos de identidad
                </h4>
                <p class="tw:text-xs tw:text-slate-500 tw:mb-3 tw:leading-relaxed">
                    Adjunta el <b>DNI/NIE/Pasaporte (ambas caras)</b> del deportista
                    y de los <b>responsables</b>.
                </p>
                <FileDropzone
                    :url="urlDocumentos"
                    :max-files="50"
                    :upload-multiple="true"
                    accepted-files="image/jpeg,image/png,image/gif,image/bmp,image/tiff,application/pdf"
                    :max-filesize="20"
                    :disabled="!urlDocumentos"
                    :uploaded-files="documentosEntries"
                    @update:uploaded-files="onUploadedFilesChange"
                    @success="(file, response) => onDocumentoUploaded(response)"
                    @remove-file="onDocumentoRemoved"
                />
                <ul v-if="documentosEntries.length" class="tw:mt-3 tw:space-y-1">
                    <li
                        v-for="(entry, index) in documentosEntries"
                        :key="index"
                        class="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:text-xs tw:text-slate-600 tw:border tw:border-slate-200 tw:rounded tw:px-2 tw:py-1"
                    >
                        <span class="tw:truncate">{{ entry.file?.name || ('Documento ' + (index + 1)) }}</span>
                        <button
                            type="button"
                            class="tw:text-red-500 hover:tw:text-red-700 tw:text-xs tw:font-semibold"
                            @click="removeDocumento(index)"
                        >
                            Quitar
                        </button>
                    </li>
                </ul>
            </div>

            <!-- ── Zona 2: Foto de perfil ── -->
            <div>
                <h4 class="tw:text-sm tw:font-semibold tw:text-slate-700 tw:mb-2">
                    Foto de perfil
                </h4>
                <p class="tw:text-xs tw:text-slate-500 tw:mb-3 tw:leading-relaxed">
                    Foto del deportista para el perfil. Debe ser una imagen
                    tipo carnet (cara visible, fondo claro).
                </p>
                <FileDropzone
                    :url="urlFoto"
                    :max-files="1"
                    :upload-multiple="false"
                    accepted-files="image/jpeg,image/png"
                    :max-filesize="20"
                    :disabled="!urlFoto"
                    @success="(file, response) => onFotoUploaded(response)"
                    @removed="() => onFotoRemoved()"
                />
            </div>
        </div>
    </ValidationObserver>
</template>

<script setup>
    import { ref, computed, inject } from 'vue';
    import FileDropzone from '@/modules/admin/ui/FileDropzone.vue';
    import { config } from '@/config';

    /**
     * StepperPasoTres — Tercer paso del wizard: documentación.
     *
     * Dos zonas de subida de archivos:
     *   - Documentos de identidad (DNI/NIE/Pasaporte, múltiples)
     *   - Foto de perfil (única)
     *
     * Recibe formData vía inject (compartido desde Index.vue).
     */
    const formData = inject('formData');

    // ── ValidationObserver ref (expuesto al padre) ──
    const observer = ref(null);

    // ── IDs y URLs de documentos subidos ──
    const documentosIds = ref([]);
    const documentosEntries = ref([]);
    const fotoUrl = ref('');
    const documentosRef = ref(null);

    // ── URLs de subida ──
    const urlDocumentos = computed(() => {
        if (!formData || !formData.dni) return '';
        return `${config.API_BASE}/f/upload/documento?tipo=bases&id=${encodeURIComponent(formData.dni)}`;
    });

    const urlFoto = computed(() => {
        return `${config.API_BASE}/f/guardar-foto-deportista`;
    });

    // ── Handlers documentos ──
    // Per-file add (FileDropzone ya emite @success por archivo y mantiene el array).
    function onDocumentoUploaded(response) {
        if (response && response.documento_id) {
            documentosIds.value.push(response.documento_id);
        }
    }

    // Per-file remove — preserves other uploaded docs.
    function onDocumentoRemoved({ index }) {
        if (typeof index === 'number') {
            documentosIds.value.splice(index, 1);
        }
    }

    // Mirror the dropzone's uploadedFiles array locally so the template can
    // render a per-file list with individual "Quitar" buttons. (Vue 2.7
    // does not support v-model with an argument, so we wire :uploaded-files
    // + @update:uploaded-files manually.)
    function onUploadedFilesChange(value) {
        documentosEntries.value = Array.isArray(value) ? value : [];
    }

    // Local per-file removal. We mirror the array in this component so we
    // don't need to keep a ref to the dropzone instance.
    function removeDocumento(index) {
        documentosEntries.value = documentosEntries.value.filter((_, i) => i !== index);
        documentosIds.value.splice(index, 1);
    }

    // ── Handlers foto ──
    function onFotoUploaded(response) {
        if (response.url) {
            fotoUrl.value = response.url;
        }
    }

    function onFotoRemoved() {
        fotoUrl.value = '';
    }

    // ── Validación expuesta al padre ──
    //   No requiere validación de formulario (los documentos son opcionales según el legacy).
    //   Solo verificamos que haya al menos foto o documentos si se quiere forzar.
    async function validate() {
        return true; // Documentación opcional (mismo comportamiento que el legacy)
    }

    // ── Método para que el padre obtenga los datos acumulados ──
    function getDocumentData() {
        return {
            documentos_id: [...documentosIds.value],
            foto_url: fotoUrl.value,
        };
    }

    defineExpose({
        get observer() { return observer.value; },
        validate,
        getDocumentData,
    });
</script>
